import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { cn } from "../lib/cn.js";
import {
  formatRange,
  matchPreset,
  presetRange,
  PRESETS,
  rangeProblem,
  toLocalDate,
  type DateRange,
  type PresetKey,
} from "../lib/dates.js";
import { Button } from "./ui/button.js";

/** `PRESETS`' first five are single/short windows; the last two are calendar months. */
const DAY_PRESETS = PRESETS.slice(0, 5);
const MONTH_PRESETS = PRESETS.slice(5);

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date: Date, delta: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + delta, 1);
}

/** `YYYY-MM-DD` as a local calendar date, the inverse of `toLocalDate`. */
function parseLocalDate(iso: string): Date {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
}

function isSameDay(a: Date | null, b: Date | null): boolean {
  return (
    a !== null &&
    b !== null &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isBetween(date: Date, a: Date, b: Date): boolean {
  const [lo, hi] = a.getTime() <= b.getTime() ? [a, b] : [b, a];
  return date.getTime() >= lo.getTime() && date.getTime() <= hi.getTime();
}

/** The range being built: a picked start with no end yet, a complete range, or neither. */
interface PendingRange {
  readonly from: Date | null;
  readonly to: Date | null;
}

type DropdownKind = "month" | "year";

/**
 * The date range control, built like legacy's `CustomDateRangePicker.jsx`: a pill showing
 * the current window, opening a panel with a Days/Months preset sidebar and a two-month
 * calendar.
 *
 * THE PRESETS ARE LEGACY'S EXACT ARITHMETIC (see `lib/dates.ts`) — "Last 7 days" is
 * `today - 6 … today`, inclusive of today. A merchant comparing the two dashboards on the
 * same afternoon must get the same window, or every number differs and neither app looks
 * trustworthy.
 *
 * SELECTION IS TWO CLICKS, WITH A LIVE PREVIEW — exactly legacy's `handleDateSelect`: the
 * first click picks the start; while the second end is still unpicked, hovering a day
 * previews the range that click would complete (a light wash between the start and the
 * pointer, both months), and the second click locks it in, chronologically ordered even
 * when clicked backwards. A third click after a complete range starts over, which is what a
 * merchant expects after finishing one range and wanting an unrelated one.
 *
 * NOTHING IS APPLIED UNTIL "Apply". A picker that refetches on every click fires a request
 * for a half-picked range while someone is still choosing the end date, and the report
 * flickers through wrong answers on the way to the right one. Escape or clicking away
 * discards.
 *
 * WHAT THIS DELIBERATELY DROPS FROM LEGACY: the "Compare" toggle (it fetches and renders a
 * SECOND range, which no report here is wired to yet — showing the checkbox with nothing
 * behind it would be a control that lies) and the "Dates are shown in Asia/Kolkata Time"
 * footer (this picker reads the BROWSER's local calendar days, not a fixed timezone — see
 * `lib/dates.ts` — so legacy's exact sentence would be a claim this app cannot back up).
 */
export function DateRangePicker({
  value,
  onApply,
}: {
  readonly value: DateRange;
  readonly onApply: (range: DateRange) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState<PendingRange>({ from: null, to: null });
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const [leftMonth, setLeftMonth] = useState<Date>(() => startOfMonth(new Date()));
  const [rightMonth, setRightMonth] = useState<Date>(() => addMonths(startOfMonth(new Date()), 1));
  const [expanded, setExpanded] = useState<"days" | "months" | null>(null);
  const [activePreset, setActivePreset] = useState<PresetKey | null>(null);
  const [openDropdown, setOpenDropdown] = useState<{ readonly side: 0 | 1; readonly kind: DropdownKind } | null>(
    null,
  );
  const root = useRef<HTMLDivElement>(null);

  /**
   * REOPENING starts from what is CURRENTLY applied, not from an abandoned edit — but this
   * must run only on the CLOSED-TO-OPEN transition, never again while the popover stays
   * open. `value` is deliberately NOT a dependency: the report screen refetches on an
   * unrelated timer (coverage, entitlements) and hands this component a new `value` object
   * with the SAME dates on every one of those re-renders, and a merchant mid-selection —
   * one click into a range, or with "Last 28 days" expanded but not yet applied — must
   * never have that in-progress choice wiped by a fetch they didn't initiate.
   */
  useEffect(() => {
    if (!open) return;
    const from = parseLocalDate(value.from);
    const to = parseLocalDate(value.to);
    setRange({ from, to });
    setHoverDate(null);
    setLeftMonth(startOfMonth(from));
    setRightMonth(addMonths(startOfMonth(from), 1));
    setOpenDropdown(null);
    const preset = matchPreset(value);
    setActivePreset(preset);
    setExpanded(
      preset === null ? null : DAY_PRESETS.some((p) => p.key === preset) ? "days" : "months",
    );
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent): void => {
      if (root.current !== null && !root.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // A month/year dropdown closes on any click outside ITS OWN trigger+menu — including a
  // click on the other calendar's dropdown, which must switch rather than stack.
  useEffect(() => {
    if (openDropdown === null) return;
    const onPointerDown = (event: PointerEvent): void => {
      if (!(event.target as HTMLElement).closest("[data-dropdown-root]")) setOpenDropdown(null);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [openDropdown]);

  const selectDate = (date: Date): void => {
    setActivePreset(null);
    if (range.from === null || range.to !== null) {
      setRange({ from: date, to: null });
      setHoverDate(date);
    } else {
      const start = range.from <= date ? range.from : date;
      const end = range.from <= date ? date : range.from;
      setRange({ from: start, to: end });
      setHoverDate(null);
    }
  };

  const hoverPreview = (date: Date): void => {
    if (range.from !== null && range.to === null) setHoverDate(date);
  };

  const applyPreset = (key: PresetKey): void => {
    const preset = presetRange(key);
    const from = parseLocalDate(preset.from);
    setRange({ from, to: parseLocalDate(preset.to) });
    setHoverDate(null);
    setActivePreset(key);
    setLeftMonth(startOfMonth(from));
    setRightMonth(addMonths(startOfMonth(from), 1));
  };

  const draftIso: DateRange | null =
    range.from !== null && range.to !== null
      ? { from: toLocalDate(range.from), to: toLocalDate(range.to) }
      : null;
  const problem = draftIso === null ? "Pick both a start and an end date." : rangeProblem(draftIso);

  const apply = (): void => {
    if (draftIso === null || rangeProblem(draftIso) !== null) return;
    onApply(draftIso);
    setOpen(false);
  };

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        // Named, because the report header now holds a second dialog trigger (the ⓘ) and
        // "the button with a popup" no longer identifies this one — for a screen reader
        // or for a test.
        aria-label="Change date range"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="bg-card hover:bg-accent inline-flex h-10 items-center gap-2.5 rounded-xl border px-4 text-sm font-medium shadow-sm transition-colors"
      >
        <CalendarDays className="text-muted-foreground size-4" />
        {formatRange(value)}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Choose a date range"
          className="bg-popover absolute right-0 z-50 mt-2 flex w-[min(50rem,95vw)] flex-col gap-0 rounded-xl border shadow-lg sm:flex-row"
        >
          <div className="flex shrink-0 flex-col gap-2 border-b p-3 sm:w-44 sm:border-r sm:border-b-0">
            <PresetSection
              label="Days"
              expanded={expanded === "days"}
              onToggle={() => setExpanded((current) => (current === "days" ? null : "days"))}
              presets={DAY_PRESETS}
              activePreset={activePreset}
              onPick={applyPreset}
            />
            <PresetSection
              label="Months"
              expanded={expanded === "months"}
              onToggle={() => setExpanded((current) => (current === "months" ? null : "months"))}
              presets={MONTH_PRESETS}
              activePreset={activePreset}
              onPick={applyPreset}
            />
            {/* Set by the calendar, not clickable — legacy's read-only "Custom" indicator. */}
            <label className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium">
              <input
                type="radio"
                readOnly
                checked={activePreset === null}
                className="accent-primary size-3.5"
              />
              Custom
            </label>
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-3 p-3">
            <div className="flex flex-col gap-4 sm:flex-row">
              <CalendarMonth
                monthDate={leftMonth}
                side={0}
                range={range}
                hoverDate={hoverDate}
                openDropdown={openDropdown?.side === 0 ? openDropdown.kind : null}
                onToggleDropdown={(kind) =>
                  setOpenDropdown((current) =>
                    current?.side === 0 && current.kind === kind ? null : { side: 0, kind },
                  )
                }
                onPickMonth={(month) => {
                  setLeftMonth((current) => new Date(current.getFullYear(), month, 1));
                  setOpenDropdown(null);
                }}
                onPickYear={(year) => {
                  setLeftMonth((current) => new Date(year, current.getMonth(), 1));
                  setOpenDropdown(null);
                }}
                onPrevious={() => setLeftMonth((current) => addMonths(current, -1))}
                onAdvance={() => setLeftMonth((current) => addMonths(current, 1))}
                onSelect={selectDate}
                onHover={hoverPreview}
              />
              <CalendarMonth
                monthDate={rightMonth}
                side={1}
                range={range}
                hoverDate={hoverDate}
                openDropdown={openDropdown?.side === 1 ? openDropdown.kind : null}
                onToggleDropdown={(kind) =>
                  setOpenDropdown((current) =>
                    current?.side === 1 && current.kind === kind ? null : { side: 1, kind },
                  )
                }
                onPickMonth={(month) => {
                  setRightMonth((current) => new Date(current.getFullYear(), month, 1));
                  setOpenDropdown(null);
                }}
                onPickYear={(year) => {
                  setRightMonth((current) => new Date(year, current.getMonth(), 1));
                  setOpenDropdown(null);
                }}
                onPrevious={() => setRightMonth((current) => addMonths(current, -1))}
                onAdvance={() => setRightMonth((current) => addMonths(current, 1))}
                onSelect={selectDate}
                onHover={hoverPreview}
              />
            </div>

            {problem !== null && (
              <p role="alert" className="text-destructive px-1 text-xs">
                {problem}
              </p>
            )}

            <div className="mt-auto flex items-center justify-end gap-2 border-t pt-3">
              <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="button" size="sm" disabled={problem !== null} onClick={apply}>
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** One expandable preset list — "Days" (Today … Last 28 days) or "Months" (This/Last Month). */
function PresetSection({
  label,
  expanded,
  onToggle,
  presets,
  activePreset,
  onPick,
}: {
  readonly label: string;
  readonly expanded: boolean;
  readonly onToggle: () => void;
  readonly presets: readonly { readonly key: PresetKey; readonly label: string }[];
  readonly activePreset: PresetKey | null;
  readonly onPick: (key: PresetKey) => void;
}): React.JSX.Element {
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="hover:bg-accent flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm font-medium transition-colors"
      >
        {label}
        <ChevronDown className={cn("size-3.5 transition-transform", expanded && "rotate-180")} />
      </button>
      {expanded && (
        <div className="mt-1 flex flex-col gap-0.5 rounded-md border p-1.5">
          {presets.map((preset) => (
            <label
              key={preset.key}
              className="hover:bg-accent flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm"
            >
              <input
                type="radio"
                name="date-range-preset"
                checked={activePreset === preset.key}
                onChange={() => onPick(preset.key)}
                className="accent-primary size-3.5"
              />
              {preset.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * One month's grid, plus its own month/year dropdown pickers and previous/next month
 * navigation arrows to easily move between months.
 * easily and without needing to open dropdown menus.
 */
function CalendarMonth({
  monthDate,
  side,
  range,
  hoverDate,
  openDropdown,
  onToggleDropdown,
  onPickMonth,
  onPickYear,
  onPrevious,
  onAdvance,
  onSelect,
  onHover,
}: {
  readonly monthDate: Date;
  readonly side: 0 | 1;
  readonly range: PendingRange;
  readonly hoverDate: Date | null;
  readonly openDropdown: DropdownKind | null;
  readonly onToggleDropdown: (kind: DropdownKind) => void;
  readonly onPickMonth: (month: number) => void;
  readonly onPickYear: (year: number) => void;
  readonly onPrevious: () => void;
  readonly onAdvance: () => void;
  readonly onSelect: (date: Date) => void;
  readonly onHover: (date: Date) => void;
}): React.JSX.Element {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const today = new Date();

  const dayOfWeek = new Date(year, month, 1).getDay();
  const firstWeekday = (dayOfWeek + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: { readonly date: Date; readonly outside: boolean }[] = [];
  for (let i = firstWeekday; i > 0; i -= 1) {
    cells.push({ date: new Date(year, month, 1 - i), outside: true });
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({ date: new Date(year, month, day), outside: false });
  }
  while (cells.length < 42) {
    const previous = cells[cells.length - 1]?.date ?? new Date(year, month, daysInMonth);
    cells.push({ date: new Date(previous.getFullYear(), previous.getMonth(), previous.getDate() + 1), outside: true });
  }

  const years = Array.from({ length: 21 }, (_, i) => today.getFullYear() - 10 + i);
  const rangeEnd = range.to ?? hoverDate;

  return (
    <div className="min-w-0 flex-1">
      <div className="mb-2 flex items-center justify-center gap-1">
        <button
          type="button"
          onClick={onPrevious}
          aria-label={side === 0 ? "Previous month" : "Previous month (right calendar)"}
          className="bg-primary text-primary-foreground mr-1 inline-flex size-6 items-center justify-center rounded-md transition-opacity hover:opacity-90"
        >
          <ChevronLeft className="size-3.5" />
        </button>
        <span data-dropdown-root className="relative">
          <button
            type="button"
            onClick={() => onToggleDropdown("month")}
            className="hover:bg-accent inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-sm font-semibold transition-colors"
          >
            {MONTH_NAMES[month]}
            <ChevronDown className="size-3.5 opacity-60" />
          </button>
          {openDropdown === "month" && (
            <div className="bg-popover absolute left-0 top-full z-10 mt-1 max-h-48 w-24 overflow-y-auto rounded-md border shadow-lg">
              {MONTH_NAMES.map((name, index) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => onPickMonth(index)}
                  className={cn(
                    "hover:bg-accent block w-full px-3 py-1.5 text-left text-sm",
                    index === month && "font-semibold",
                  )}
                >
                  {name}
                </button>
              ))}
            </div>
          )}
        </span>

        <span data-dropdown-root className="relative">
          <button
            type="button"
            onClick={() => onToggleDropdown("year")}
            className="hover:bg-accent inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-sm font-semibold transition-colors"
          >
            {year}
            <ChevronDown className="size-3.5 opacity-60" />
          </button>
          {openDropdown === "year" && (
            <div className="bg-popover absolute left-0 top-full z-10 mt-1 max-h-48 w-20 overflow-y-auto rounded-md border shadow-lg">
              {years.map((y) => (
                <button
                  key={y}
                  type="button"
                  onClick={() => onPickYear(y)}
                  className={cn("hover:bg-accent block w-full px-3 py-1.5 text-left text-sm", y === year && "font-semibold")}
                >
                  {y}
                </button>
              ))}
            </div>
          )}
        </span>

        <button
          type="button"
          onClick={onAdvance}
          aria-label={side === 0 ? "Next month" : "Next month (right calendar)"}
          className="bg-primary text-primary-foreground ml-1 inline-flex size-6 items-center justify-center rounded-md transition-opacity hover:opacity-90"
        >
          <ChevronRight className="size-3.5" />
        </button>
      </div>

      <div className="text-muted-foreground mb-1 grid grid-cols-7 text-center text-xs font-medium">
        {WEEKDAYS.map((day) => (
          <div key={day}>{day}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-y-0.5">
        {cells.map(({ date, outside }) => {
          const isToday = isSameDay(date, today);
          const isStart = isSameDay(date, range.from);
          const isEnd = isSameDay(date, range.to);
          const inRange = !outside && !isStart && !isEnd && range.from !== null && rangeEnd !== null && isBetween(date, range.from, rangeEnd);
          return (
            <button
              key={date.toISOString()}
              type="button"
              disabled={outside}
              data-date={outside ? undefined : toLocalDate(date)}
              onClick={() => onSelect(date)}
              onMouseEnter={() => onHover(date)}
              className={cn(
                "aspect-square rounded-md text-sm transition-colors",
                outside
                  ? "text-muted-foreground/30 cursor-default"
                  : "cursor-pointer",
                // Hover is a PLAIN day's affordance only. Tailwind emits `hover:` rules
                // after plain utilities, so a bare `hover:bg-accent` sitting on every cell
                // would win over `bg-blue-50` the instant the pointer crossed a range day —
                // legacy never has this flash, because its `.in-range` rule is the more
                // specific one. Simplest fix: an in-range/start/end cell never gets the
                // hover class at all, so there is nothing for the pointer to change.
                !outside && !inRange && !isStart && !isEnd && "hover:bg-accent",
                isToday && !isStart && !isEnd && "text-primary font-semibold",
                // A light blue wash, not the neutral `bg-accent` hover grey — legacy's
                // `.calendar-day.in-range` is `#d9eaff` with its own rounded corners on
                // EVERY cell, not one flat unrounded strip across the whole range.
                inRange && "bg-[#d9eaff]",
                (isStart || isEnd) && "bg-primary! text-primary-foreground! font-medium",
              )}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
